import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Car, Users, FileText, AlertCircle, Wrench, AlertTriangle } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';

// A failed query must never render as a zero — every count on this page is a
// number people act on, so surface the failure instead of a plausible-looking 0.
function QueryErrorState({ title, error, className }: { title: string; error: unknown; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center text-destructive', className)}>
      <AlertTriangle className="mb-2 h-5 w-5" />
      <p className="text-sm font-medium">{title}</p>
      {/* PostgREST errors are not always Error instances — read .message off the cast. */}
      <p className="mt-1 text-xs text-muted-foreground break-words">
        {(error as Error)?.message || String(error)}
      </p>
    </div>
  );
}

export default function Portfolio() {
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['portfolio-stats'],
    queryFn: async () => {
      const [vehiclesRes, driversRes, maintenanceRes] = await Promise.all([
        supabase.from('vehicles').select('id', { count: 'exact' }),
        supabase.from('drivers').select('id', { count: 'exact' }),
        supabase.from('vehicle_maintenances').select('id', { count: 'exact' }).is('ended_at', null),
      ]);
      if (vehiclesRes.error) throw vehiclesRes.error;
      if (driversRes.error) throw driversRes.error;
      if (maintenanceRes.error) throw maintenanceRes.error;

      // Get active contracts count using RPC that understands contract_updates
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: contractsData, error: contractsError } = await (supabase as any).rpc('get_contracts_with_status', {
        status_filter: 'IN_PROGRESS',
        page_from: 0,
        page_to: 0,
      }) as { data: { count: number } | null; error: Error | null };
      if (contractsError) throw contractsError;

      return {
        totalVehicles: vehiclesRes.count || 0,
        totalDrivers: driversRes.count || 0,
        activeContracts: contractsData?.count || 0,
        pendingMaintenance: maintenanceRes.count || 0,
      };
    },
  });

  // Alerts removed - overdue interest feature deprecated

  // Outstanding balances have no read to call. `financial_summary` was a pre-production function that does not
  // exist on this database, so calling it failed on every visit. The estate's totals must be the server's sums of
  // the get_contract_insights scalars, never a recompute here, and fleet-backend owes that read — until then the
  // panel says so and makes no request.

  const { data: contractsByState, isLoading: contractsByStateLoading, error: contractsByStateError } = useQuery({
    queryKey: ['contracts-by-state'],
    queryFn: async () => {
      // Use RPC to get counts by status
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const [inProgress, completed, terminated] = await Promise.all([
        (supabase as any).rpc('get_contracts_with_status', { status_filter: 'IN_PROGRESS', page_from: 0, page_to: 0 }),
        (supabase as any).rpc('get_contracts_with_status', { status_filter: 'COMPLETED', page_from: 0, page_to: 0 }),
        (supabase as any).rpc('get_contracts_with_status', { status_filter: 'TERMINATED', page_from: 0, page_to: 0 }),
      ]) as { data: { count: number } | null; error: Error | null }[];

      const rpcError = inProgress.error ?? completed.error ?? terminated.error;
      if (rpcError) throw rpcError;

      return [
        { name: 'Active', value: inProgress.data?.count || 0 },
        { name: 'Completed', value: completed.data?.count || 0 },
        { name: 'Terminated', value: terminated.data?.count || 0 },
      ].filter(item => item.value > 0);
    },
  });

  const pieColors = [
    'hsl(215, 25%, 45%)',
    'hsl(160, 25%, 45%)',
    'hsl(35, 30%, 50%)',
  ];

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Portfolio</h1>
        <p className="text-muted-foreground">All-time fleet overview and status</p>
      </div>

      {/* Stats Cards */}
      {statsError ? (
        <QueryErrorState
          title="Failed to load fleet stats"
          error={statsError}
          className="py-12"
        />
      ) : (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Vehicles</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{stats?.totalVehicles || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Drivers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{stats?.totalDrivers || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Contracts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{stats?.activeContracts || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Maintenance</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{stats?.pendingMaintenance || 0}</div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Portfolio Status & Contract Status */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Status</CardTitle>
            <CardDescription>Outstanding balances</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-[150px] items-center justify-center text-muted-foreground">
              <AlertCircle className="mr-2 h-4 w-4" />These totals are not on this database yet.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contract Status</CardTitle>
            <CardDescription>By state</CardDescription>
          </CardHeader>
          <CardContent>
            {contractsByStateError ? (
              <QueryErrorState
                title="Failed to load contract statuses"
                error={contractsByStateError}
                className="h-[200px]"
              />
            ) : contractsByStateLoading ? (
              <div className="flex h-[200px] items-center justify-center">
                <Skeleton className="h-[160px] w-[160px] rounded-full" />
              </div>
            ) : contractsByState && contractsByState.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={contractsByState} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                      {contractsByState.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1 mt-2">
                  {contractsByState.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: pieColors[index % pieColors.length] }} />
                        <span className="text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                <AlertCircle className="mr-2 h-4 w-4" />No data
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}